import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '@dixa/lib-shared-habla';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { DixaLogo } from 'components/dixaLogo';
import { useAppSelector } from 'utils/redux/useAppSelector';
import styles from './appStatus.module.scss';
import { appErrors } from './utils/appErrors';
const loadingStatusMessage = 'Loading...';
const loadingLongStatusMessage = 'This is taking longer than usual...';
/**
 * The delay in milliseconds before the `loading longer than usual` status
 * message gets displayed.
 */
const loadingLongDelay = 10000; // 10s.
/** A component that displays the app status screen. */
export const AppStatus = () => {
    const timeoutHandle = useRef();
    const [progressBarFillScaleX, setProgressBarFillScaleX] = useState(0);
    const [pendingLong, setPendingLong] = useState(false);
    const { error, appAsyncStatus, progress } = useAppSelector((state) => ({
        appAsyncStatus: state.app.asyncStatus.app,
        error: state.app.error,
        progress: {
            presence: state.app.asyncStatus.presence,
            featureFlags: state.app.asyncStatus.featureFlags,
            webSocket: state.websocket.meta.asyncCreateStatus,
        },
    }), shallowEqual);
    // Governs the timer for when the app is pending too long.
    useEffect(() => {
        if (appAsyncStatus === 'pending' && !timeoutHandle.current) {
            timeoutHandle.current = window.setTimeout(() => setPendingLong(true), loadingLongDelay);
        }
        else {
            window.clearTimeout(timeoutHandle.current);
        }
        return () => window.clearTimeout(timeoutHandle.current);
    }, [appAsyncStatus]);
    // Advances the progress bar as progress members are fulfilling.
    useEffect(() => {
        if (error) {
            setProgressBarFillScaleX(0);
        }
        else {
            const totalProgress = Object.keys(progress).length;
            const currentProgress = Object.keys(progress).filter((progressKey) => progress[progressKey] === 'fulfilled').length;
            setProgressBarFillScaleX(
            // We +1 to ensure the progress appears to have advanced in the start.
            (currentProgress + 1) / (totalProgress + 1));
        }
    }, [progress, error]);
    /** Renders the progress status. */
    const progressStatus = useMemo(() => {
        if (!error) {
            return (_jsxs("div", Object.assign({ className: styles.progressStatus }, { children: [_jsx("div", Object.assign({ className: styles.progressBar }, { children: _jsx("div", { className: styles.progressBarFill, style: {
                                transform: `scaleX(${progressBarFillScaleX})`,
                            } }) })), _jsx("div", Object.assign({ className: styles.status }, { children: pendingLong ? loadingLongStatusMessage : loadingStatusMessage }))] })));
        }
        return undefined;
    }, [error, progressBarFillScaleX, pendingLong]);
    /** Handles the error action button. */
    const handleErrorAction = useCallback((action, event) => {
        if (action.type === 'link') {
            window.open(action.url, '_blank', 'noreferrer');
        }
        else {
            action.onClick(event);
        }
    }, []);
    /** Renders the error message. */
    const errorMessage = useMemo(() => {
        if (error) {
            const { title, description, actions } = appErrors[error.type];
            const actionElements = !!(actions === null || actions === void 0 ? void 0 : actions.length) && (_jsx("div", Object.assign({ className: styles.errorActions }, { children: actions.map((action, index) => (_jsx(Button, Object.assign({ onClick: (event) => handleErrorAction(action, event) }, { children: action.label }), index))) })));
            return (_jsxs("div", Object.assign({ className: styles.error }, { children: [_jsx("div", Object.assign({ className: styles.errorTitle }, { children: title })), _jsx("div", Object.assign({ className: styles.errorDescription }, { children: description })), actionElements] })));
        }
        return undefined;
    }, [error, handleErrorAction]);
    return (_jsx("div", Object.assign({ className: styles.root }, { children: _jsxs("div", Object.assign({ className: styles.container }, { children: [_jsx(DixaLogo, { className: styles.logo }), progressStatus, errorMessage] })) })));
};
